/*
 * BOGOF free-print picker
 * -----------------------
 * Shopper picks which print they want free before add-to-cart; the pick is added
 * as a second line once the main product lands in the cart.
 *
 * Ordering reuses the personalisation model from assets/bundle-builder.js:258-345
 * so the two surfaces rank prints the same way: recently viewed first (localStorage
 * `yp_recent`, written by snippets/recently-viewed-tracker.liquid), then best
 * sellers, new arrivals and tag affinity, with anything already in the cart pushed
 * down. Recently-viewed prints missing from the server-rendered pool are hydrated
 * from /products/<handle>.js, same as the bundle builder does.
 *
 * How the free print actually reaches the cart:
 * We do NOT add it ourselves on click, and we do NOT add the main product ourselves.
 * SlideCart owns add-to-cart (it hijacks the click with stopImmediatePropagation),
 * and snippets/framing-selector.liquid already adds the optional frame by chaining
 * window.SLIDECART_OPENED. We chain the same callback, so main product, frame and
 * free print all compose instead of fighting. Chaining (not overwriting) matters:
 * framing-selector and assets/bogof-cart-nudge.js both hook the same callbacks.
 *
 * SlideCart monkey-patches window.fetch, so the add goes through a pristine fetch
 * taken from a hidden iframe.
 */
(function () {
  'use strict';

  var MAX_TILES = 6;
  var W = { RECENT: 100, BESTSELLER: 40, NEW: 25, AFFINITY: 15, JITTER: 5, CART: 200, PICKED: 10000 };
  var BADGE_LABELS = { recent: 'Just viewed' };

  /* ---------- pristine fetch ---------- */
  var _nativeFetch = null;
  function getNativeFetch() {
    if (_nativeFetch) return Promise.resolve(_nativeFetch);
    return new Promise(function (resolve) {
      var iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = 'about:blank';
      iframe.addEventListener('load', function () {
        _nativeFetch = iframe.contentWindow.fetch.bind(iframe.contentWindow);
        resolve(_nativeFetch);
      });
      document.body.appendChild(iframe);
    });
  }

  function money(pence) {
    return '£' + (pence / 100).toFixed(2).replace(/\.00$/, '');
  }

  function readRecent() {
    try {
      var arr = JSON.parse(localStorage.getItem('yp_recent') || '[]');
      return Array.isArray(arr) ? arr.filter(function (r) { return r && r.handle; }) : [];
    } catch (e) {
      return [];
    }
  }

  class BogofPicker extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-bogof-grid]');
      this.statusEl = this.querySelector('[data-bogof-status]');
      this.sizeMap = this.parseSizes();
      this.priceMap = this.parseJSON(this.dataset.currentPrices);
      this.pendingVariantId = null;
      this.pendingTitle = null;

      this.form = document.querySelector('form[action*="/cart/add"]');
      this.cards = Array.from(this.querySelectorAll('.bogof-card'));

      this.onCardClick = this.onCardClick.bind(this);
      this.grid.addEventListener('click', this.onCardClick);

      /* Size can change after load. iOS Safari fires `change` (not `input`) on
         selects and radios, so listen for change. */
      this.onVariantChange = this.render.bind(this);
      document.addEventListener('change', this.onVariantChange);
      if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
        try { window.subscribe(window.PUB_SUB_EVENTS.variantChange, this.onVariantChange); } catch (e) {}
      }

      this.inCartHandles = new Set();
      this.hookAddToCart();
      Promise.all([this.hydrateRecent(), this.loadCartHandles()]).then(this.render.bind(this));
    }

    /* Prints already in the cart are pushed down the ranking, so the picker does
       not offer a design the shopper has just added. Best effort: a failure here
       only costs us the ranking penalty. */
    loadCartHandles() {
      var self = this;
      return getNativeFetch()
        .then(function (f) { return f('/cart.js', { credentials: 'same-origin' }); })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (cart) self.inCartHandles = new Set((cart.items || []).map(function (i) { return i.handle; }));
        })
        .catch(function () {});
    }

    parseSizes() {
      return this.parseJSON(this.dataset.currentSizes);
    }

    parseJSON(raw) {
      try { return JSON.parse(raw || '{}'); } catch (e) { return {}; }
    }

    currentVariantId() {
      var input = this.form && this.form.querySelector('[name="id"]');
      return input && input.value;
    }

    /* The size currently chosen on this PDP, derived from the form's variant id
       rather than from the swatch label, so it survives any markup change. */
    currentSize() {
      var id = this.currentVariantId();
      return (id && this.sizeMap[id]) || null;
    }

    variantForSize(card, size) {
      if (!size) return null;
      var key = 'variant' + size.toUpperCase();
      var v = card.dataset[key];
      return v && v !== '' ? v : null;
    }

    priceForSize(card, size) {
      if (!size) return null;
      var p = Number(card.dataset['price' + size.toUpperCase()]);
      return p > 0 ? p : null;
    }

    /* "£15 FREE" under each tile, in the chosen size. Rewritten only when it
       changes, since render() runs on every size change. */
    paintPrice(card, size) {
      var el = card.querySelector('[data-bogof-price]');
      if (!el) return;
      var pence = this.priceForSize(card, size);
      var key = pence ? String(pence) : '';
      if (el.dataset.for === key) return;
      el.dataset.for = key;
      el.innerHTML = pence
        ? '<s>' + money(pence) + '</s> <span class="bogof-card__free">FREE</span>'
        : '';
      if (pence) el.setAttribute('aria-label', 'Usually ' + money(pence) + ', free');
      else el.removeAttribute('aria-label');
    }

    /* Pull in recently-viewed prints that the server-rendered pool missed, so a
       print the shopper just looked at can still be offered. Mirrors
       assets/bundle-builder.js:92-127. */
    hydrateRecent() {
      var recent = readRecent();
      if (!recent.length) return Promise.resolve();

      var present = new Set(this.cards.map(function (c) { return c.dataset.productHandle; }));
      present.add(this.dataset.currentHandle);
      var missing = recent.filter(function (r) { return !present.has(r.handle); }).slice(0, 6);
      if (!missing.length) return Promise.resolve();

      var self = this;
      return getNativeFetch()
        .then(function (f) {
          return Promise.all(missing.map(function (r) {
            return f('/products/' + encodeURIComponent(r.handle) + '.js')
              .then(function (res) { return res.ok ? res.json() : null; })
              .catch(function () { return null; });
          }));
        })
        .then(function (products) {
          products.forEach(function (p) {
            if (!p) return;
            var tags = (p.tags || []).map(String);
            /* Same eligibility gate as offer-badge.liquid and the bogof-prints
               collection, so an ineligible recent (frame, candle, gift card,
               personalised print) is never offered as a freebie. */
            var eligible = tags.some(function (t) {
              return t === 'bundle-a4' || t === 'bundle-a3' || t === 'bundle-a2';
            });
            if (!eligible) return;
            var card = self.buildCard(p, tags);
            if (card) {
              self.grid.insertBefore(card, self.grid.firstChild);
              self.cards.push(card);
            }
          });
        })
        .catch(function () {});
    }

    buildCard(p, tags) {
      var bySize = {};
      var priceBySize = {};
      (p.variants || []).forEach(function (v) {
        if (v.available && ['A4', 'A3', 'A2'].indexOf(v.title) !== -1) {
          bySize[v.title] = v.id;
          priceBySize[v.title] = v.price;
        }
      });
      if (!Object.keys(bySize).length) return null;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bogof-card';
      btn.dataset.productHandle = p.handle;
      btn.dataset.productTitle = p.title;
      btn.dataset.variantA4 = bySize.A4 || '';
      btn.dataset.variantA3 = bySize.A3 || '';
      btn.dataset.variantA2 = bySize.A2 || '';
      btn.dataset.priceA4 = priceBySize.A4 || '';
      btn.dataset.priceA3 = priceBySize.A3 || '';
      btn.dataset.priceA2 = priceBySize.A2 || '';
      btn.dataset.tags = tags.join(',');
      btn.dataset.bestSeller = '0';
      btn.dataset.newArrival = '0';
      btn.setAttribute('aria-pressed', 'false');
      btn.hidden = true;

      var img = p.featured_image ? String(p.featured_image).replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '';
      btn.innerHTML =
        '<span class="bogof-card__media">' +
          (img ? '<img src="' + img + '" alt="" loading="lazy" width="240" height="240">' : '') +
          '<span class="bogof-card__badge" hidden></span>' +
          '<span class="bogof-card__check" aria-hidden="true">' +
            '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 10 8 14 16 6"></polyline></svg>' +
          '</span>' +
        '</span>' +
        '<span class="bogof-card__title"></span>' +
        '<span class="bogof-card__price" data-bogof-price></span>';
      btn.querySelector('.bogof-card__title').textContent = p.title.replace(' - Print', '');
      return btn;
    }

    render() {
      var size = this.currentSize();
      var self = this;

      var recent = readRecent();
      var recentRank = new Map(recent.map(function (r, i) { return [r.handle, recent.length - i]; }));

      /* Tag affinity, built from the recently-viewed prints in our own pool. */
      var recentTags = new Set();
      this.cards.forEach(function (c) {
        if (recentRank.has(c.dataset.productHandle)) {
          (c.dataset.tags || '').split(',').forEach(function (t) {
            var trimmed = t.trim();
            if (trimmed) recentTags.add(trimmed);
          });
        }
      });

      var eligible = this.cards.filter(function (c) { return self.variantForSize(c, size); });

      /* Hide everything, reset badges, then reveal the winners. */
      this.cards.forEach(function (c) {
        c.hidden = true;
        var b = c.querySelector('.bogof-card__badge');
        if (b) { b.textContent = ''; b.hidden = true; }
      });

      if (!size || !eligible.length) {
        this.hidden = true;
        return;
      }

      var scored = eligible.map(function (card) {
        var handle = card.dataset.productHandle;
        var tags = (card.dataset.tags || '').split(',').map(function (t) { return t.trim(); }).filter(Boolean);
        var overlap = tags.reduce(function (acc, t) { return acc + (recentTags.has(t) ? 1 : 0); }, 0);
        /* render() runs on every size and quantity change. A fresh Math.random() each
           time reshuffled the tiles and silently dropped the shopper's pick, so the
           jitter is drawn once per tile, and the picked design is pinned. */
        if (card._ypJitter === undefined) card._ypJitter = Math.random();
        var score =
          W.RECENT * (recentRank.get(handle) || 0) +
          W.BESTSELLER * (card.dataset.bestSeller === '1' ? 1 : 0) +
          W.NEW * (card.dataset.newArrival === '1' ? 1 : 0) +
          W.AFFINITY * overlap +
          W.JITTER * card._ypJitter +
          W.PICKED * (handle === self.pendingHandle ? 1 : 0) -
          W.CART * (self.inCartHandles && self.inCartHandles.has(handle) ? 1 : 0);
        return { card: card, score: score, isRecent: recentRank.has(handle) };
      }).sort(function (a, b) { return b.score - a.score; });

      scored.slice(0, MAX_TILES).forEach(function (item, idx) {
        item.card.hidden = false;
        self.paintPrice(item.card, size);
        if (idx < 3 && item.isRecent) {
          var b = item.card.querySelector('.bogof-card__badge');
          if (b) { b.textContent = BADGE_LABELS.recent; b.hidden = false; }
        }
      });

      this.hidden = false;
      this.syncSelection(size);
    }

    /* Keep the pending pick valid across size changes: re-resolve it to the same
       product in the new size, or drop it if that print has no such size. */
    syncSelection(size) {
      if (!this.pendingHandle) { this.paintSelection(); return; }
      var card = this.cards.find(function (c) { return c.dataset.productHandle === this.pendingHandle; }, this);
      var vid = card ? this.variantForSize(card, size) : null;
      if (!vid || card.hidden) {
        this.clearSelection();
      } else {
        this.pendingVariantId = vid;
        this.paintSelection();
      }
    }

    onCardClick(event) {
      var card = event.target.closest('.bogof-card');
      if (!card) return;
      event.preventDefault();

      var size = this.currentSize();
      var vid = this.variantForSize(card, size);
      if (!vid) return;

      if (this.pendingHandle === card.dataset.productHandle) {
        this.clearSelection();
        return;
      }
      this.pendingHandle = card.dataset.productHandle;
      this.pendingVariantId = vid;
      this.pendingTitle = card.dataset.productTitle;
      this.paintSelection();
    }

    clearSelection() {
      this.pendingHandle = null;
      this.pendingVariantId = null;
      this.pendingTitle = null;
      this.paintSelection();
    }

    paintSelection() {
      var self = this;
      this.cards.forEach(function (c) {
        var on = c.dataset.productHandle === self.pendingHandle;
        c.classList.toggle('is-selected', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.statusEl) this.statusEl.textContent = this.statusText();
    }

    /*
     * Spell out what the pair costs ("2 prints for £15"): a total is a stronger
     * confirmation than "free" alone. The cheapest unit is the free one, so the pair
     * costs the dearer of the two. Only claimed at quantity 1, where "2 prints" is
     * literally true; otherwise just confirm the pick.
     */
    statusText() {
      if (!this.pendingTitle) return '';
      var name = this.pendingTitle.replace(' - Print', '');
      var size = this.currentSize();
      var card = this.cards.find(function (c) { return c.dataset.productHandle === this.pendingHandle; }, this);
      var pick = card ? this.priceForSize(card, size) : null;
      var main = Number(this.priceMap[this.currentVariantId()]) || null;
      var qtyInput = document.querySelector('.product-form__quantity input[name="quantity"]');
      var qty = qtyInput ? Number(qtyInput.value) || 1 : 1;
      if (pick && main && qty === 1) {
        return name + ' comes free: 2 prints for ' + money(Math.max(pick, main)) + '.';
      }
      return name + ' comes free with this print.';
    }

    /*
     * Chain SLIDECART_OPENED so the free print is added right after SlideCart has
     * added the main product. Chaining rather than assigning: framing-selector
     * and bogof-cart-nudge hook the same callback.
     */
    hookAddToCart() {
      var self = this;
      var prev = window.SLIDECART_OPENED;
      window.SLIDECART_OPENED = function () {
        if (typeof prev === 'function') {
          try { prev.apply(this, arguments); } catch (e) {}
        }
        self.addPending();
      };
      /* Fallback for any non-SlideCart path (e.g. app disabled). */
      document.addEventListener('cart:refresh', function () { self.addPending(); });
    }

    addPending() {
      if (!this.pendingVariantId) return;
      var id = this.pendingVariantId;
      var self = this;
      /* Clear first so a re-entrant callback cannot double-add. */
      this.clearSelection();

      getNativeFetch()
        .then(function (f) {
          return f('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1, properties: { '_bogof_free_pick': 'true' } }] })
          });
        })
        .then(function () {
          if (typeof window.SLIDECART_UPDATE === 'function') {
            try { window.SLIDECART_UPDATE(); } catch (e) {}
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return self.loadCartHandles().then(function () { self.render(); });
        })
        .catch(function () {});
    }
  }

  if (!window.customElements.get('bogof-picker')) {
    window.customElements.define('bogof-picker', BogofPicker);
  }
})();
